import { useEffect, useRef } from 'react';
import _ from 'lodash';
import { useDebouncedCallback } from 'use-debounce';
import { findChangedFormValues } from '../utils';
import { shouldSkipFormChangeFieldKey } from './form-change-handler-common';
import { findEditsSurvivingReinitialize } from './findEditsSurvivingReinitialize';
import { FormInitialValues } from '../typings';
import {
  flushAllSaveQueues,
  flushSaveQueuesOnUnload,
  getSaveQueue,
  peekSaveQueue,
  unmarkSentValue,
} from '../save-queue';
import type { FormChangeCallback } from '../save-queue';
import { config } from '../config';
import { config as appConfig } from '@/config';
import { getStoreState } from '@/store';

/** Decides which edits may reach the save queue, and reports the ones held back. */
export type FormChangeTrackerGate = {
  /** False when the API would refuse this value; the edit is not queued. */
  shouldPersist: (fieldId: string, value: FormInitialValues[string]) => boolean;
  /** Called for each edit held back. */
  onBlocked: (fieldId: string, value: FormInitialValues[string]) => void;
  /** Called for each edit that is queued, so the blocks it supersedes can be lifted. */
  onCleared: (fieldId: string, value: FormInitialValues[string]) => void;
  /** Called when Formik reinitialized, with the values the reset left on screen. */
  onReinitialized?: (values: FormInitialValues) => void;
};

// Stable identity for callers that never reset a field, so the default cannot look
// like a fresh marker on every render.
const NO_FIELD_RESET_IDS: readonly string[] = [];

/**
 * Feeds every value change into the form's save queue and owns the triggers that
 * drain it. The queue owns batching, ordering and retries — this hook only
 * computes the diff and decides when to flush.
 */
export const useFormChangeTracker = (
  uniqueKey: string,
  values: FormInitialValues,
  dirty: boolean,
  onChange: FormChangeCallback | undefined,
  reinitializeCount: number,
  initialValues: FormInitialValues,
  gate?: FormChangeTrackerGate,
  fieldResetCount = 0,
  fieldResetIds: readonly string[] = NO_FIELD_RESET_IDS,
) => {
  const previousValuesRef = useRef(_.cloneDeep(values));
  const hasBeenDirtyRef = useRef(false);
  const keyRef = useRef(uniqueKey);
  // The last `reinitializeCount` the baseline was synced against.
  const reinitializeCountRef = useRef(reinitializeCount);
  // The last `fieldResetCount` the baseline was synced against.
  const fieldResetCountRef = useRef(fieldResetCount);
  // Tracks the last `values` reference the diff effect actually ran against, so a
  // render that re-fires this effect only because `onChange` got a fresh identity
  // (it isn't structurally stable — see callers) can be told apart from a render
  // where Formik's `values` genuinely changed. See the diff effect below.
  const valuesRef = useRef(values);

  // Safety net so a user who types without ever blurring, navigating or hiding
  // the tab still gets saved. It only schedules a drain — the values are already
  // safely in the module-level queue, so losing this timer costs nothing.
  // Takes the key as an argument rather than reading `keyRef` at fire time: on a
  // section-to-section navigation the ref has already moved on, and the timer
  // would drain the new form's queue instead of the one it was scheduled for.
  const scheduleFlush = useDebouncedCallback(
    (key: string) => {
      void peekSaveQueue(key)?.flushWaiting();
    },
    config.formChangeTrackerDebounceTime,
    { maxWait: config.formChangeTrackerMaxWait },
  );

  useEffect(() => {
    if (keyRef.current !== uniqueKey) {
      keyRef.current = uniqueKey;
      hasBeenDirtyRef.current = false;
      previousValuesRef.current = _.cloneDeep(values);
      // Keep this in sync too, or a reinitialize that happened on the OLD key
      // (still unconsumed) could be mistaken for one on the new key's first
      // real edit and swallow it — see the diff effect below.
      reinitializeCountRef.current = reinitializeCount;
      // Same reasoning for the per-field marker: an unconsumed reset from the old
      // key must not fold the new key's first edit into its baseline.
      fieldResetCountRef.current = fieldResetCount;
    }
  }, [uniqueKey, values, reinitializeCount, fieldResetCount]);

  useEffect(() => {
    if (dirty) {
      hasBeenDirtyRef.current = true;
    }
  }, [dirty]);

  useEffect(() => {
    // Read-only trackers (no onChange) never diff or enqueue anything — bail before
    // touching the latch below so they don't deep-clone `values` on every reinit.
    if (!onChange) return;

    // `onChange` is not structurally stable (see callers — it's often recreated every
    // render of the parent), so this effect can re-fire on a render where `values`
    // itself hasn't changed; the diff would be empty then, so it is skipped. The two
    // reset markers below still run on such a render, and their comments say why.
    const valuesChanged = valuesRef.current !== values;
    valuesRef.current = values;

    // The per-field sibling of the reinitialize latch further down. `resetFields` calls
    // Formik's setFieldValue and bumps `fieldResetCount` in one synchronous block, so
    // React 18 batches both into a single commit: the render carrying the new count
    // already carries the reset values, and Formik is a child of the component owning
    // the count, so it cannot render ahead of it either. Waiting for a `values` change
    // here would be wrong in the one case where a reset writes a value the field already
    // holds — Formik's setIn returns the SAME `values` object, no `values` change ever
    // arrives, and the marker would be consumed later against whatever the provider
    // typed next, silently swallowing that edit. So `fieldResetCount` is a dependency of
    // this effect, consumed here, above the `valuesChanged` guard.
    if (fieldResetCountRef.current !== fieldResetCount) {
      fieldResetCountRef.current = fieldResetCount;

      // Folded into the baseline instead of diffed: the value written is the page-load
      // `initialValues` snapshot, which an ordinary successful save leaves stale (that
      // response carries no section, so nothing rewrites it). Sending it would put a
      // value the provider already replaced back over the server's. The refetch the
      // discard fires alongside is the only thing allowed to move these fields.
      fieldResetIds.forEach(fieldId => {
        // Own-property only, for the same reason resetFields checks: an id like
        // `__proto__` would otherwise write through the baseline's prototype.
        if (!Object.prototype.hasOwnProperty.call(values, fieldId)) return;
        previousValuesRef.current[fieldId] = _.cloneDeep(values[fieldId]);
      });
    }

    // Shared by the latch branch and the ordinary diff so both hand the queue exactly the
    // same thing: the queue owns delivery and retries from here on.
    const enqueueChanges = (changedValues: FormInitialValues) => {
      // Legacy void-returning callbacks count as accepted; only explicit false is a failure.
      const queue = getSaveQueue(
        uniqueKey,
        async changes => (await onChange(changes)) !== false,
      );
      let enqueued = false;

      Object.entries(changedValues).forEach(([fieldId, value]) => {
        // Validation-only keys never reach the server; queuing one starts a save round
        // that sends nothing but still flips the chip to "Saving…".
        if (shouldSkipFormChangeFieldKey(fieldId)) return;

        // Held back rather than sent and retried: the API answers a value it refuses
        // with a 400 that the queue cannot tell apart from a transport failure.
        if (gate && !gate.shouldPersist(fieldId, value)) {
          gate.onBlocked(fieldId, value);
          return;
        }

        gate?.onCleared(fieldId, value);
        // An earlier request's mark may predate a server override of that value, so a
        // re-edit stays client-owned until a request sends it.
        unmarkSentValue(uniqueKey, fieldId);
        queue.enqueue(fieldId, value);
        enqueued = true;
      });

      // Skipped keys alone must not push back the timer of an edit already waiting.
      if (enqueued) scheduleFlush(uniqueKey);
    };

    // Formik reset `values` to a server snapshot. DynamicForm counts each reset as it happens
    // and reads the count during Formik's render, so the count moves on exactly the render
    // that first shows the reset; values the fields wrote before it, as the snapshot reshaped
    // them, came under the old count. Consumed here, above the `valuesChanged` guard, like
    // the marker above.
    if (reinitializeCountRef.current !== reinitializeCount) {
      reinitializeCountRef.current = reinitializeCount;

      // A block whose field the reset removed describes text nobody can see or fix. Runs
      // before the enqueue below, which may legitimately re-block an edit that survived.
      gate?.onReinitialized?.(values);

      const current = { ...values };
      delete current['#'];

      // Formik has just reset `values` to `initialValues`. Anything still different is a
      // user edit that landed inside the cascade; folding it into the baseline unchanged
      // is what used to swallow it. An untouched reset produces {} here, so the echo-write
      // fix this latch exists for is unaffected.
      const survivingEdits = findEditsSurvivingReinitialize(
        values,
        initialValues,
      );

      previousValuesRef.current = _.cloneDeep(current);

      if (hasBeenDirtyRef.current && Object.keys(survivingEdits).length > 0) {
        enqueueChanges(survivingEdits);
      }

      return;
    }

    if (!valuesChanged || !hasBeenDirtyRef.current) return;

    const current = { ...values };
    delete current['#'];

    const changedValues = findChangedFormValues(
      current,
      previousValuesRef.current,
    );

    if (Object.keys(changedValues).length === 0) return;

    // Advance the baseline as soon as the queue owns the value; the queue, not this
    // hook, is responsible for getting it to the server and retrying if it fails.
    previousValuesRef.current = _.cloneDeep(current);

    enqueueChanges(changedValues);
    // fieldResetIds is excluded only because it is redundant: it never changes without
    // fieldResetCount changing with it, so the run that consumes the marker always closes
    // over the ids from that same render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uniqueKey,
    values,
    onChange,
    scheduleFlush,
    initialValues,
    gate,
    fieldResetCount,
    reinitializeCount,
  ]);

  // These triggers live here, not in a page component, because every form that
  // tracks changes needs them — SectionPage, AttestationFormContent,
  // MissingItemsPage and the login form all share this hook.
  useEffect(() => {
    const flushNow = () => {
      void flushAllSaveQueues();
    };

    const flushIfHidden = () => {
      if (document.visibilityState === 'hidden') {
        flushNow();
      }
    };

    // `visibilitychange` fires BEFORE `pagehide` on a tab close, so by now the axios
    // flush above has already moved everything out of `pending` and into a request the
    // browser is about to kill. `takeUnacknowledged` is what reclaims it; an ordinary
    // POST here would be killed the same way.
    const flushOnUnload = () => {
      flushSaveQueuesOnUnload({
        baseUrl: appConfig.apiUrl,
        token: getStoreState().auth.token,
      });
    };

    document.addEventListener('visibilitychange', flushIfHidden);
    window.addEventListener('pagehide', flushOnUnload);

    return () => {
      document.removeEventListener('visibilitychange', flushIfHidden);
      window.removeEventListener('pagehide', flushOnUnload);

      scheduleFlush.cancel();
      // The queue lives outside React, so a request started here still completes
      // after this component unmounts. No navigation blocking needed.
      flushNow();
    };
  }, [scheduleFlush]);
};
